import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { zipToState } from "@/lib/zipToState";
import { loadPrefill, savePrefill } from "@/lib/prefill";
import { track, EVENTS } from "@/lib/analytics";
import {
    Compass, MapPin, Phone, ExternalLink, ArrowRight, Stethoscope, Home,
    HeartPulse, Sun, Users2, HeartHandshake, Landmark, Shield, Wallet,
    Scale, Building2, LifeBuoy, Search,
} from "lucide-react";

/* Resources — "Connect me with people and services."
   Action-oriented help, not education. One primary next step per card. */

const GROUPS = [
    {
        label: "Find care near you",
        items: [
            { key: "guide-providers", Icon: Stethoscope, title: "GUIDE providers", desc: "Medicare GUIDE programs accepting families — dementia support at no cost with Original Medicare.", to: "/resources?category=guide#guide-providers", cta: "Find a GUIDE program" },
            { key: "neurology", Icon: HeartPulse, title: "Neurology & memory clinics", desc: "Specialists who diagnose and manage dementia. Compare doctors and clinics near you.", href: "https://www.medicare.gov/care-compare/", cta: "Search Medicare Care Compare", also: { label: "Which specialist does what?", to: "/help-finder" } },
            { key: "home-health", Icon: Home, title: "Home health", desc: "Nurses and therapists who come to the home — usually after a hospital stay or with a doctor's order.", href: "https://www.medicare.gov/care-compare/?providerType=HomeHealth", cta: "Compare home health agencies", also: { label: "Is home health right for us?", to: "/help-finder" } },
            { key: "hospice", Icon: HeartHandshake, title: "Hospice", desc: "Comfort-focused care in the late stage — at home or in a facility, covered by Medicare.", href: "https://www.medicare.gov/care-compare/?providerType=Hospice", cta: "Compare hospice providers", also: { label: "When is hospice appropriate?", to: "/help-finder" } },
            { key: "adult-day", Icon: Sun, title: "Adult day programs", desc: "Safe, social daytime care for your loved one — and real respite hours for you.", href: "https://eldercare.acl.gov/", cta: "Find programs (Eldercare Locator)", also: { label: "How to choose a program", to: "/resources/choosing-adult-day-program" } },
        ],
    },
    {
        label: "Support & community",
        items: [
            { key: "support-groups", Icon: Users2, title: "Support groups", desc: "People who get it. In-person and online groups for dementia caregivers.", to: "/resources?category=support_group", cta: "Browse caregiver groups", also: { label: "Alzheimer's Association groups", href: "https://www.alz.org/help-support/community/support-groups" } },
            { key: "alz-association", Icon: HeartHandshake, title: "Alzheimer's Association", desc: "Free 24/7 helpline staffed by dementia specialists — any question, any hour, any stage.", href: "https://www.alz.org", cta: "Visit alz.org", phone: { label: "24/7 Helpline: 1-800-272-3900", tel: "18002723900" } },
            { key: "community", Icon: Building2, title: "Local community services", desc: "Meals, transportation, respite, and in-home help through your Area Agency on Aging.", href: "https://eldercare.acl.gov/", cta: "Eldercare Locator", phone: { label: "1-800-677-1116 (weekdays)", tel: "18006771116" } },
        ],
    },
    {
        label: "Money & legal",
        items: [
            { key: "medicare-medicaid", Icon: Shield, title: "Medicare & Medicaid", desc: "What's covered, what isn't, and how to apply for help with long-term care costs.", href: "https://www.medicare.gov", cta: "Medicare.gov", also: { label: "What each program pays for", to: "/who-pays" } },
            { key: "va-benefits", Icon: Landmark, title: "VA benefits", desc: "Veterans and surviving spouses may qualify for dementia care, Aid & Attendance, and respite.", href: "https://www.va.gov/geriatrics/", cta: "VA geriatrics & extended care" },
            { key: "financial", Icon: Wallet, title: "Financial assistance", desc: "One screening finds benefits you may be missing — utilities, food, prescriptions, care.", href: "https://benefitscheckup.org", cta: "BenefitsCheckUp (free, NCOA)", also: { label: "Paying for care, explained", to: "/who-pays" } },
            { key: "legal", Icon: Scale, title: "Legal resources", desc: "Power of attorney, advance directives, and guardianship — best done early.", href: "https://www.nia.nih.gov/health/legal-and-financial-planning-people-dementia", cta: "Legal planning guide (NIA)", also: { label: "Power of attorney, step by step", to: "/hard-conversations/power-of-attorney" } },
        ],
    },
];

function CrisisStrip() {
    return (
        <div className="ml-card p-5 bg-coral-light/50 !border-coral-softer" data-testid="resources-hub-crisis">
            <div className="flex items-center gap-2 mb-2">
                <LifeBuoy size={17} className="text-coral-dark" />
                <h2 className="font-heading font-semibold text-[#1F2624]">If you need help right now</h2>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-[#1F2624]">
                <span><b>Medical emergency:</b> call 911</span>
                <span><b>Overwhelmed or in crisis:</b> call or text <a href="tel:988" className="font-semibold text-coral-dark underline" data-testid="resources-hub-988">988</a> (Suicide &amp; Crisis Lifeline)</span>
                <span><b>Dementia questions, any hour:</b> <a href="tel:18002723900" className="font-semibold text-coral-dark underline" data-testid="resources-hub-alz-line">1-800-272-3900</a> (Alzheimer&apos;s Association, free 24/7)</span>
            </div>
        </div>
    );
}

function ZipFinder() {
    const [zip, setZip] = useState("");
    const navigate = useNavigate();
    // Phase 4 data reuse — remember/offer the caregiver's ZIP.
    useEffect(() => {
        loadPrefill().then(p => { if (p.zip) setZip(z => z || p.zip); });
    }, []);
    const go = (e) => {
        e.preventDefault();
        const st = zipToState(zip.trim());
        if (st) savePrefill({ zip: zip.trim(), state: st });
        track(EVENTS.FIND_LOCAL_RESOURCES, { source: "resources_hub", state: st || "unknown" });
        navigate(st ? `/resources?state=${st}` : "/resources");
    };
    return (
        <form onSubmit={go} className="ml-card p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="resources-hub-zip-form">
            <div className="flex items-center gap-2 text-sm font-medium text-[#1F2624] shrink-0">
                <MapPin size={16} className="text-teal-dark" /> Find what&apos;s near you
            </div>
            <input
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="ZIP code"
                inputMode="numeric"
                className="ml-input !min-h-[44px] sm:max-w-[160px]"
                data-testid="resources-hub-zip"
            />
            <button type="submit" className="ml-btn-primary !min-h-[44px] !px-5 text-sm" data-testid="resources-hub-zip-go">
                <Search size={15} /> Search local directory
            </button>
        </form>
    );
}

function ResourceCard({ item }) {
    const Primary = item.to
        ? <Link to={item.to} className="ml-btn-primary !min-h-[40px] !px-4 text-[13px] inline-flex items-center gap-1.5" data-testid={`resources-hub-cta-${item.key}`}>{item.cta} <ArrowRight size={13} /></Link>
        : <a href={item.href} target="_blank" rel="noopener noreferrer" className="ml-btn-primary !min-h-[40px] !px-4 text-[13px] inline-flex items-center gap-1.5" data-testid={`resources-hub-cta-${item.key}`}>{item.cta} <ExternalLink size={13} /></a>;
    return (
        <div className="ml-card p-5 flex flex-col" data-testid={`resources-hub-cat-${item.key}`}>
            <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal-soft text-teal-dark shrink-0">
                    <item.Icon size={17} strokeWidth={1.7} />
                </span>
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-[#1F2624]">{item.title}</h3>
                    <p className="text-xs text-[#4C5551] mt-0.5 leading-snug">{item.desc}</p>
                </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                {Primary}
                {item.phone && (
                    <a href={`tel:${item.phone.tel}`} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-teal-dark hover:underline">
                        <Phone size={13} /> {item.phone.label}
                    </a>
                )}
                {item.also && (item.also.to
                    ? <Link to={item.also.to} className="text-[13px] text-teal-dark underline hover:text-teal">{item.also.label}</Link>
                    : <a href={item.also.href} target="_blank" rel="noopener noreferrer" className="text-[13px] text-teal-dark underline hover:text-teal">{item.also.label}</a>)}
            </div>
        </div>
    );
}

export default function ResourcesHub() {
    return (
        <main className="min-h-screen bg-cream-light">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 md:py-12 space-y-8" data-testid="resources-hub">
                <PageHero
                    testId="resources-hub-hero"
                    kicker="Resources"
                    title="Connect with people and services."
                    subtitle="Action-oriented help — real programs, real people, real phone numbers. When you want to understand something first, Learn is one tab over."
                />
                <CrisisStrip />
                <ZipFinder />
                {GROUPS.map((g) => (
                    <section key={g.label} data-testid={`resources-hub-group-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <h2 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#6E7A74] mb-3 flex items-center gap-2">
                            <Compass size={13} className="text-teal-dark" /> {g.label}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {g.items.map((it) => <ResourceCard key={it.key} item={it} />)}
                        </div>
                    </section>
                ))}
                <p className="text-xs text-[#6E7A74]">
                    Looking for the Medicare GUIDE program specifically? It has its own room: <Link to="/guide" className="underline text-teal-dark" data-testid="resources-hub-to-guide">GUIDE</Link>.
                    External sites open in a new tab; Memory Lane doesn&apos;t receive anything for listing them.
                </p>
            </div>
        </main>
    );
}
